import { ChainType } from "@0xsquid/squid-types";
import { UseQueryOptions, UseQueryResult } from "@tanstack/react-query";
import BigNumber from "bignumber.js";
import { useMemo } from "react";
import { SwapDirection } from "../../core/types/swap";
import { TokenWithBalance } from "../../core/types/tokens";
import { useWallet } from "../wallet/useWallet";
import {
  AllTokensBalanceQueryData,
  useAllTokensWithBalanceForChainType,
} from "./useAllTokensWithBalanceForChainType";

const BALANCE_STALE_TIME = 60000; // 1 minute
const BALANCE_CACHE_TIME = 300000; // 5 minutes

type ChainTypeToBalanceQueryMap = Record<
  ChainType,
  UseQueryResult<AllTokensBalanceQueryData>
>;

export const useAllConnectedWalletBalances = ({
  direction,
  queryOptions = {
    staleTime: BALANCE_STALE_TIME,
    cacheTime: BALANCE_CACHE_TIME,
    refetchOnWindowFocus: true,
    refetchOnMount: true,
  },
}: {
  direction?: SwapDirection;
  queryOptions?: UseQueryOptions<{
    tokens: TokenWithBalance[];
    totalUsdBalance: string;
  }>;
} = {}) => {
  const { connectedAddresses } = useWallet();

  const evmBalancesQuery = useAllTokensWithBalanceForChainType({
    chainType: ChainType.EVM,
    address: connectedAddresses?.[ChainType.EVM],
    direction,
    queryOptions,
  });

  const cosmosBalancesQuery = useAllTokensWithBalanceForChainType({
    chainType: ChainType.COSMOS,
    address: connectedAddresses?.[ChainType.COSMOS],
    direction,
    queryOptions,
  });

  const bitcoinBalancesQuery = useAllTokensWithBalanceForChainType({
    chainType: ChainType.BTC,
    address: connectedAddresses?.[ChainType.BTC],
    direction,
    queryOptions,
  });

  const solanaBalancesQuery = useAllTokensWithBalanceForChainType({
    chainType: ChainType.SOLANA,
    address: connectedAddresses?.[ChainType.SOLANA],
    direction,
    queryOptions,
  });

  const suiBalancesQuery = useAllTokensWithBalanceForChainType({
    chainType: ChainType.SUI,
    address: connectedAddresses?.[ChainType.SUI],
    direction,
    queryOptions,
  });

  const xrplBalancesQuery = useAllTokensWithBalanceForChainType({
    chainType: ChainType.XRPL,
    address: connectedAddresses?.[ChainType.XRPL],
    direction,
    queryOptions,
  });

  const stellarBalancesQuery = useAllTokensWithBalanceForChainType({
    chainType: ChainType.STELLAR,
    address: connectedAddresses?.[ChainType.STELLAR],
    direction,
    queryOptions,
  });

  const cantonBalancesQuery = useAllTokensWithBalanceForChainType({
    chainType: ChainType.CANTON,
    address: connectedAddresses?.[ChainType.CANTON],
    direction,
    queryOptions,
  });

  // Create a map of chain type to balance query results
  const balanceQueries = useMemo<ChainTypeToBalanceQueryMap>(
    () => ({
      [ChainType.EVM]: evmBalancesQuery,
      [ChainType.COSMOS]: cosmosBalancesQuery,
      [ChainType.BTC]: bitcoinBalancesQuery,
      [ChainType.SOLANA]: solanaBalancesQuery,
      [ChainType.SUI]: suiBalancesQuery,
      [ChainType.XRPL]: xrplBalancesQuery,
      [ChainType.STELLAR]: stellarBalancesQuery,
      [ChainType.CANTON]: cantonBalancesQuery,
    }),
    [
      evmBalancesQuery,
      cosmosBalancesQuery,
      bitcoinBalancesQuery,
      solanaBalancesQuery,
      suiBalancesQuery,
      xrplBalancesQuery,
      stellarBalancesQuery,
      cantonBalancesQuery,
    ]
  );

  // Combine all tokens from different chains
  const allTokens = useMemo(
    () =>
      Object.values(balanceQueries).flatMap(
        (query) => query.data?.tokens ?? []
      ),
    [balanceQueries]
  );

  // Calculate total USD balance across all chains
  const totalUsdBalance = useMemo(() => {
    return Object.values(balanceQueries)
      .reduce((total, query) => {
        const balanceStr = query?.data?.totalUsdBalance;
        if (!balanceStr) return total;

        const balance = new BigNumber(balanceStr);
        return balance.isFinite() ? total.plus(balance) : total;
      }, new BigNumber(0))
      .toString();
  }, [balanceQueries]);

  // Aggregate loading states
  const queryStates = useMemo(
    () => ({
      isInitialLoading: Object.values(balanceQueries).some(
        (q) => q.isInitialLoading
      ),
      isFetching: Object.values(balanceQueries).some((q) => q.isFetching),
      isLoading: Object.values(balanceQueries).some((q) => q.isLoading),
      isRefetching: Object.values(balanceQueries).some((q) => q.isRefetching),
      isError: Object.values(balanceQueries).some((q) => q.isError),
      isSuccess: Object.values(balanceQueries).every((q) => q.isSuccess),
    }),
    [balanceQueries]
  );

  return {
    ...queryStates,
    tokens: allTokens,
    totalUsdBalance,
  };
};
